import { type FunctionComponent, useEffect, useRef } from 'react';

import { getAudioClientService } from 'services';

import './style.scss';

// When the waveform is read into byte-array (with 1 byte per measurement),
// the center point of the waveform is 127.5, and the signal amplitude thus
// able to be between 0.5 and 127.5. Here are constants for this values.
const MAX = 127.5;
const MID = 127.5;
const MIN = 0.5;

const LOG2_MAX = Math.log2(MAX);
const LOG2_MIN = Math.log2(MIN);

type PropsT = {
  onEnded?: () => void;
  source: AudioBuffer | MediaStream | null | undefined;
  withOutput?: boolean;
};

const InputVolume: FunctionComponent<PropsT> = ({
  onEnded,
  source,
  withOutput,
}) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let sourceNode: AudioBufferSourceNode | MediaStreamAudioSourceNode
      | undefined;
    let timerId: number | undefined = 0;

    void (async () => {
      const a = await getAudioClientService();

      if (source) {
        const context = a.getAudioContext();
        const analyser = new AnalyserNode(context);

        sourceNode = source instanceof AudioBuffer
          ? new AudioBufferSourceNode(context, { buffer: source })
          : new MediaStreamAudioSourceNode(context, { mediaStream: source });

        sourceNode.connect(analyser);

        const buffer = new Uint8Array(analyser.fftSize);

        const update = () => {
          analyser.getByteTimeDomainData(buffer);

          let rms = 0;
          for (const value of buffer) rms += (value - MID) ** 2;
          rms = Math.sqrt(rms / buffer.length);

          const volume = (Math.log2(rms) - LOG2_MIN) / (LOG2_MAX - LOG2_MIN);
          if (ref.current) ref.current.style.width = `${100 * volume}%`;

          if (context.state !== 'suspended') {
            timerId = requestAnimationFrame(update);
            return;
          }

          void (async () => {
            await context.resume();
            if (timerId) timerId = requestAnimationFrame(update);
          })();
        };

        if (timerId === 0) timerId = requestAnimationFrame(update);

        if (withOutput) analyser.connect(context.destination);

        if (sourceNode instanceof AudioBufferSourceNode) {
          if (onEnded) sourceNode.addEventListener('ended', onEnded);
          if (withOutput) sourceNode.start();
        }
      }
    })();

    return () => {
      if (timerId !== undefined) {
        if (withOutput && sourceNode instanceof AudioBufferSourceNode) {
          sourceNode.stop();
        }
        cancelAnimationFrame(timerId);
        timerId = undefined;
      }
    };
  }, [onEnded, source, withOutput]);

  return (
    <div styleName="container">
      <div ref={ref} styleName="bar" />
    </div>
  );
};

export default InputVolume;
